import { Component, computed, inject, input, signal } from "@angular/core";
import {
  ItemMenu,
  NavBarService,
  SubItemMenu,
} from "@app/layouts/nav-bar/nav-bar.service";
import { NavigationEnd, Router } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { delay, filter } from "rxjs/operators";
import { NgClass } from "@angular/common";
import { OverlayModule } from "@angular/cdk/overlay";
import { PopupMinimizedNavBarComponent } from "@app/layouts/nav-bar/popup-minimized-nav-bar/popup-minimized-nav-bar.component";
import { SubListNavBarComponent } from "@app/layouts/nav-bar/sub-list-nav-bar/sub-list-nav-bar.component";
import { animate, style, transition, trigger } from "@angular/animations";
import { DxSharedModule } from "@app/shared/dx-shared.module";

@Component({
  selector: "app-item-button-nav-bar",
  standalone: true,
  imports: [
    NgClass,
    OverlayModule,
    PopupMinimizedNavBarComponent,
    SubListNavBarComponent,
    DxSharedModule,
  ],
  templateUrl: "./item-button-nav-bar.component.html",
  styleUrl: "./item-button-nav-bar.component.scss",
  animations: [
    trigger("popup2", [
      transition(":enter", [
        style({ opacity: 0, transform: "translateY(-10px)" }),
        animate(
          "200ms ease-out",
          style({ opacity: 1, transform: "translateY(0)" })
        ),
      ]),
      transition(":leave", [
        animate(
          "150ms ease-in",
          style({ opacity: 0, transform: "translateY(-10px)" })
        ),
      ]),
    ]),
  ],
})
export class ItemButtonNavBarComponent {
  public readonly listItem = input.required<ItemMenu>();
  protected readonly subItemsPopupOpen = signal<boolean>(false);
  protected readonly subItemsForPopup = signal<SubItemMenu[]>([]);
  private readonly mainLayoutService = inject(NavBarService);
  protected readonly isMinimized =
    this.mainLayoutService.getSignalIsMinimized();
  protected readonly positions = this.mainLayoutService.positions;
  private readonly router = inject(Router);
  private readonly currentUrl = signal(this.router.url);
  protected readonly isChildRouteActive = computed(() => {
    const item = this.listItem();
    const children = item.children;
    if (!children?.length) return false;

    const url = this.currentUrl().split("?")[0].split("#")[0];

    // Совпадение по URL родительского элемента
    const parentPath = "/" + item.url.filter((s) => s !== "/").join("/");
    if (url === parentPath || url.startsWith(parentPath + "/")) {
      return true;
    }

    return children.some((child) => {
      const segments = child.url.filter((s) => s !== "/");
      const childPath = "/" + segments.join("/");

      // Точное совпадение или вложенный маршрут
      if (url === childPath || url.startsWith(childPath + "/")) {
        return true;
      }

      // Совпадение по базовому пути (для маршрутов не указанных в children)
      if (segments.length > 1) {
        const basePath = "/" + segments.slice(0, -1).join("/");
        return url.startsWith(basePath + "/");
      }

      return false;
    });
  });
  protected readonly isActiveState = computed(() => {
    if (this.isMinimized()) {
      return this.listItem().expanded || this.isChildRouteActive();
    } else {
      return this.listItem().expanded;
    }
  });

  public constructor() {
    this.mainLayoutService.closeAllPopups$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.closeSubItemsPopup();
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        delay(200),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.currentUrl.set(this.router.url);
        // this.closeSubItemsPopup();
      });
  }

  protected onHandleClick(): void {
    if (!this.isMinimized()) {
      this.mainLayoutService.toggleExpandedItemByHeader(this.listItem().header);
    }
  }

  protected onMouseEnterInButton(subItems: SubItemMenu[] | undefined): void {
    if (this.isMinimized() && subItems?.length) {
      this.mainLayoutService.closeAllPopups();
      this.subItemsForPopup.set(subItems);
      this.subItemsPopupOpen.set(true);
    }
  }

  protected closeSubItemsPopup(): void {
    this.subItemsPopupOpen.set(false);
  }
}
